import { AbstractProduct, ConcreteProduct, DeserializedAbstractProduct, ProductPrice } from '@/src/lib/product/types';
import { DeserializedObject } from '@/src/lib/http/jsonapi/deserializer';
import { camelCaseizeConcreteProduct, isDeserializedConcreteProduct } from '@/src/lib/product/business/product-item';
import { DeserializedConcreteProduct } from '@/src/lib/catalog-search/types';

export function calculateFullScalePrice(priceInSmallScale: number, scale: number = 100): number {
  return Number((priceInSmallScale / scale).toFixed(2));
}

export function getNetPriceWithCurrency(prices: Array<ProductPrice>, locale: string) {
  const price = getNetPrice(prices);

  if (!price || !price.netAmount) return undefined;

  return getFormattedPriceWithLocaleCurrency(price.netAmount, price.currency.code, locale);
}

export function getNetPrice(prices: Array<ProductPrice>) {
  const price = prices.find((price) => price.priceTypeName === 'DEFAULT');
  if (!price || !price?.netAmount) return undefined;

  return price;
}

export function getNetPriceAsFloat(prices: Array<ProductPrice>) {
  const price = getNetPrice(prices);

  if (!price || !price.netAmount) return undefined;

  return calculateFullScalePrice(price.netAmount).toFixed(2);
}

export function getFormattedPriceWithLocaleCurrency(price: number, currency: string, locale: string) {
  let priceWithDecimals = price;
  if (priceWithDecimals % 1 === 0) {
    priceWithDecimals = calculateFullScalePrice(price);
  }

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });

  return formatter.format(priceWithDecimals);
}

export function getCurrencyFromAbstractProduct(product: AbstractProduct) {
  if (!product.prices?.[0].currency?.code) {
    throw new Error('Product has no prices');
  }
  return product.prices[0].currency.code;
}

export function getCurrencyFromConcreteProduct(product: ConcreteProduct | DeserializedConcreteProduct): string | never {
  if (isDeserializedConcreteProduct(product)) {
    return product.concreteProductPrices[0].prices[0].currency.code;
  }

  const camelCaseizedProduct = camelCaseizeConcreteProduct(product);
  if (!camelCaseizedProduct.concreteProductPrices?.[0].prices?.[0].currency?.code) {
    throw new Error('Product has no prices or currency missing');
  }

  return camelCaseizedProduct.concreteProductPrices[0].prices[0].currency.code;
}

export function getCurrencyFromDeserializedAbstractProduct(
  product: DeserializedObject<DeserializedAbstractProduct> | DeserializedConcreteProduct,
) {
  if (!product.abstractProductPrices?.[0].prices?.[0].currency?.code) {
    throw new Error('Product has no prices');
  }

  return product.abstractProductPrices[0].prices[0].currency.code;
}
